import React from 'react'
import { fetchAdminQueueRows, fetchNudgeMetrics } from '../../../lib/nudgeApi'
import { NudgeStatusBadge } from '../components/NudgeStatusBadge'
import { BusinessDetailModal } from './BusinessDetailModal'
import type { AdminQueueRow, NudgeMetrics } from '../../../types/api'
import type { NudgeStatus } from '../../../types/enums'

const ALL_STATUSES: NudgeStatus[] = [
  'PENDING', 'AI_PROCESSING', 'AI_READY', 'SENDING',
  'SENT', 'FAILED', 'REPLY_RECEIVED', 'CONV_ACTIVE',
  'RECOVERED', 'RETURNED', 'NO_SHOW', 'OPT_OUT', 'EXHAUSTED',
]

const SUMMARY_CARDS: { key: NudgeStatus | 'TOTAL'; label: string; sub: string; accent?: string }[] = [
  { key: 'TOTAL',          label: 'Total',         sub: 'Across all statuses' },
  { key: 'PENDING',        label: 'Pending',       sub: 'Awaiting AI claim' },
  { key: 'AI_PROCESSING',  label: 'Generating',    sub: 'In flight to LLM' },
  { key: 'AI_READY',       label: 'Ready to send', sub: 'Email Sender will pick up' },
  { key: 'SENT',           label: 'Sent',          sub: 'Delivered to inbox' },
  { key: 'FAILED',         label: 'Failed',        sub: 'Needs attention', accent: 'var(--red)' },
]

const TIME_RANGES: { label: string; hours?: number }[] = [
  { label: '24h',  hours: 24 },
  { label: '7d',   hours: 24 * 7 },
  { label: '30d',  hours: 24 * 30 },
  { label: 'All' },
]

const PAGE_SIZE = 20

export function AdminQueuePage() {
  const [metrics, setMetrics] = React.useState<NudgeMetrics | null>(null)
  const [rows, setRows] = React.useState<AdminQueueRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [statusFilter, setStatusFilter] = React.useState<NudgeStatus | ''>('')
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [sinceHours, setSinceHours] = React.useState<number | undefined>(24 * 7)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [refreshedAt, setRefreshedAt] = React.useState<Date | null>(null)
  const [detailId, setDetailId] = React.useState<string | null>(null)

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [search])

  React.useEffect(() => { setPage(0) }, [statusFilter, debouncedSearch, sinceHours])

  const load = React.useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      fetchNudgeMetrics().catch(() => null),
      fetchAdminQueueRows({
        page,
        size: PAGE_SIZE,
        ...(statusFilter   ? { status: statusFilter }   : {}),
        ...(debouncedSearch? { q: debouncedSearch }     : {}),
        ...(sinceHours     ? { sinceHours }             : {}),
      }),
    ])
      .then(([m, q]) => {
        if (m) setMetrics(m)
        setRows(q.content ?? [])
        setTotal(q.totalElements ?? 0)
        setRefreshedAt(new Date())
      })
      .catch(() => setError('Failed to load queue.'))
      .finally(() => setLoading(false))
  }, [page, statusFilter, debouncedSearch, sinceHours])

  React.useEffect(() => { load() }, [load])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Header refreshedAt={refreshedAt} loading={loading} onRefresh={load} />

      {/* Summary KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
        {SUMMARY_CARDS.map(c => {
          const count = c.key === 'TOTAL'
            ? (metrics ? ALL_STATUSES.reduce((s, st) => s + (metrics[st] ?? 0), 0) : 0)
            : (metrics?.[c.key] ?? 0)
          const selected = c.key !== 'TOTAL' && statusFilter === c.key
          return (
            <button
              key={c.key}
              onClick={() => {
                if (c.key === 'TOTAL') { setStatusFilter(''); return }
                setStatusFilter(selected ? '' : c.key)
              }}
              style={{
                padding: '14px 16px',
                background: 'var(--surface)',
                border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10,
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
                {c.label}
              </div>
              <div style={{
                fontSize: 22, fontWeight: 600,
                color: count > 0 && c.accent ? c.accent : 'var(--text)',
                marginTop: 6, lineHeight: 1.1,
              }}>
                {count.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{c.sub}</div>
            </button>
          )
        })}
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {/* Filter bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 360 }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by business name or contact email…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 30px 6px 30px',
                fontSize: 13,
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <svg
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 14, height: 14, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '2px 6px' }}>×</button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as NudgeStatus | '')}
            style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--text)' }}
          >
            <option value="">All statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}>
            {TIME_RANGES.map(r => (
              <button
                key={r.label}
                onClick={() => setSinceHours(r.hours)}
                style={{
                  padding: '4px 10px',
                  fontSize: 12,
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: sinceHours === r.hours ? 'var(--surface)' : 'transparent',
                  color: sinceHours === r.hours ? 'var(--text)' : 'var(--text3)',
                  fontWeight: sinceHours === r.hours ? 600 : 400,
                }}
              >
                {r.label}
              </button>
            ))}
          </div>

          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'row' : 'rows'}`}
          </span>
        </div>

        {/* Table */}
        {error && (
          <div style={{ padding: '20px 16px' }}>
            <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>{error}</div>
            <button className="btn-primary" onClick={load} style={{ padding: '4px 12px', fontSize: 12 }}>Retry</button>
          </div>
        )}

        {!error && !loading && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
              No queue rows match these filters
            </div>
            <div style={{ fontSize: 12 }}>Try a wider time range or a different status.</div>
          </div>
        )}

        {!error && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Status', 'Business', 'Contact', 'Seq', 'Subject', 'Updated', ''].map(h => (
                    <th key={h} style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontSize: 11,
                      color: 'var(--text3)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr
                    key={r.id}
                    onClick={() => setDetailId(r.businessId)}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .12s ease' }}
                  >
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <NudgeStatusBadge status={r.status as NudgeStatus} />
                      {r.status === 'FAILED' && r.failedStage && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--red)' }}>· {r.failedStage}</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text)', whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.businessName}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>{r.contactName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{r.contactEmail}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text2)', textAlign: 'center' }}>{r.sequenceNumber}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text2)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.emailSubject ?? <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text3)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {relativeTime(r.updatedAt)}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text3)' }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              style={{ padding: '4px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1, color: 'var(--text2)' }}
            >← Prev</button>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{page + 1} of {totalPages}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              style={{ padding: '4px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.5 : 1, color: 'var(--text2)' }}
            >Next →</button>
          </div>
        )}
      </div>

      {detailId && <BusinessDetailModal businessId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  )
}

function Header({ refreshedAt, loading, onRefresh }: { refreshedAt: Date | null; loading: boolean; onRefresh: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>Nudge queue</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
          Every row in the cross-business <code style={{ fontFamily: "'DM Mono', monospace" }}>nudge_queue</code> pipeline. Click a row to drill into the business.
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          {refreshedAt ? `Updated ${refreshedAt.toLocaleTimeString()}` : ''}
        </span>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '5px 12px',
            fontSize: 12,
            color: 'var(--text2)',
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  )
}

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.round(diff)}s ago`
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}
